import isElectron from 'is-electron';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logoutRequest } from '../../api/sessions';
import SirenIcon from '../../assets/icons/siren-white-outlined.svg';
import BrazilIcon from '../../assets/icons/flags/brazil-round.svg';
import NetherlandsIcon from '../../assets/icons/flags/netherlands-round.svg';
import HeadphonesIcon from '../../assets/icons/headphones-white-outlined.svg';
import SettingsIcon from '../../assets/icons/square-rounded-badge-outlined.svg';
import HieroglyphsIcon from '../../assets/icons/hieroglyphs-white.svg';
import LockOpenIcon from '../../assets/icons/lock-open.svg';
import SpainIcon from '../../assets/icons/flags/spain-round.svg';
import SwedishIcon from '../../assets/icons/flags/sweden-round.svg';
import EnglishIcon from '../../assets/icons/flags/uk-round.svg';
import FinnishIcon from '../../assets/icons/flags/finland-round.svg';
import UsIcon from '../../assets/icons/flags/us-round.svg';
import BigFloatButton from '../../components/BigFloatButton/BigFloatButton';
import { Layout } from '../../components/Layout/Layout';
import { Page } from '../../components/Page/Page';
import { ProfilePicture } from '../../components/ProfilePicture/ProfilePicture';
import { isTestApiUrlApplied, removeTestApiUrl } from '../../utils/testApi';
import { isDevRelease } from '../../config/app';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { isMultiOrgFeatureEnabled } from '../../utils/features';
import i18n, { ELanguages } from '../../i18n';
import { useConfirmation } from '../../utils/ConfirmationServiceContext/confirmationContext';
import { selectUser } from '../Login/LoginSlice';
import { refreshUser } from '../Login/LoginSlice/actionCreators';
import {
  SContainer,
  SElementContainer,
  SFlag,
  SFlexDiv,
  SInfo,
  SInfoContainer,
  SLanguageBtnRightIconPlaceholder,
  SMultiOrgBannerSection,
  SSidebarLogoutFooter,
  SName,
  SRowContainers,
  SSidebar,
} from './Sidebar.styles';
import { MultiOrgActiveBanner } from '../../components/MultiOrgActiveBanner/MultiOrgActiveBanner';
import { useMultiOrgActiveTenantBanner } from '../../utils/customHooks/useMultiOrgActiveTenantBanner';
import SidebarElement, { defaultArrowIcon } from './SidebarElement';
import type { SidebarSection } from './SidebarElement';
import { logoutUser, signOutFromActiveTenantSession, tryDeletePushToken } from './actionCreators';
import { selectActiveTenantId, selectAllTenantSessions } from '../Organizations/OrganizationsSlice';
import SidebarAvailabilityButton from '../../components/SidebarAvailabilityButton/SidebarAvailabilityButton';
import { legacyImageUrlsMapper } from '../../api/legacyCommonMappers';
import { EnvironmentEnum } from '../../config/region';

function Sidebar() {
  const confirm = useConfirmation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const allTenantSessions = useAppSelector(selectAllTenantSessions);
  const activeTenantId = useAppSelector(selectActiveTenantId);
  const { shouldShowActiveTenantBanner, tenantName, email } = useMultiOrgActiveTenantBanner();

  const profilePictureFileName = useMemo(() => {
    if (!user?.imageUrls?.small) return '';
    return legacyImageUrlsMapper.fromCoreToLegacy(user.imageUrls);
  }, [user?.imageUrls]);

  useEffect(() => {
    dispatch(refreshUser());
  }, []);

  const showDevOptions = (): boolean => {
    if (isElectron()) return false;

    const userEmail = user?.email || '';
    const environment = process.env.REACT_APP_ENV_NAME as EnvironmentEnum;
    if (window.location.host.includes('localhost') || environment === EnvironmentEnum.DEV) {
      return (
        userEmail.endsWith('@example.com') ||
        userEmail.endsWith('@cosafe.com') ||
        userEmail.endsWith('@openid.example.com')
      );
    }

    return false;
  };

  const flags = {
    [ELanguages.se]: SwedishIcon,
    [ELanguages.en]: EnglishIcon,
    [ELanguages.nl]: NetherlandsIcon,
    [ELanguages.fi]: FinnishIcon,
    [ELanguages.es]: SpainIcon,
    [ELanguages.pt]: BrazilIcon,
    [ELanguages['en-TT']]: UsIcon,
  };

  const flagKey = i18n.language as 'se' | 'en' | 'fi' | 'es' | 'pt' | 'en-TT';

  const sidebarElements: SidebarSection[] = [
    {
      nameTx: 'sidebar_settings_link',
      icon: SettingsIcon,
      to: 'settings',
      rightIcon: defaultArrowIcon,
    },
    {
      nameTx: 'support',
      icon: HeadphonesIcon,
      to: 'support',
      rightIcon: defaultArrowIcon,
    },
    {
      nameTx: 'language',
      icon: HieroglyphsIcon,
      to: 'language',
      rightIcon: (
        <SLanguageBtnRightIconPlaceholder>
          <SFlag src={flags[flagKey]} alt={flagKey} />
          {defaultArrowIcon}
        </SLanguageBtnRightIconPlaceholder>
      ),
    },
  ];

  if (isElectron()) {
    sidebarElements.push({
      nameTx: 'profile_test_alarm',
      icon: SirenIcon,
      to: 'test-alarm',
      rightIcon: defaultArrowIcon,
    });
  }

  if (showDevOptions()) {
    sidebarElements.push({
      nameTx: 'Dev Options',
      icon: LockOpenIcon,
      to: 'dev-options',
      rightIcon: defaultArrowIcon,
    });
  }

  const doLogout = async (_removeTestApiUrl?: boolean) => {
    let hasSwitchedSession = false;
    if (isMultiOrgFeatureEnabled()) {
      const res = await dispatch(signOutFromActiveTenantSession());
      hasSwitchedSession = res.hasSwitchedSession;
    } else {
      await logoutRequest();
      await tryDeletePushToken();
      dispatch(logoutUser());
    }

    if (_removeTestApiUrl) {
      removeTestApiUrl();
    }

    if (hasSwitchedSession) {
      navigate('/dashboard');
    } else {
      location.reload();
    }
  };

  const confirmLogout = () => {
    const showMultiOrgLogoutCopy =
      isMultiOrgFeatureEnabled() && allTenantSessions.length > 1 && activeTenantId != null;

    confirm({
      title: showMultiOrgLogoutCopy ? 'tenant_session_logout_title' : 'warning',
      description: showMultiOrgLogoutCopy
        ? 'tenant_session_logout_description'
        : 'login_logout_question',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onSubmit: doLogout,
      onCancel: () => {},
      confirmText: 'login_logoutButton',
      cancelText: 'cancel',
    });
  };

  const handleRemoveTestApiUrl = () => doLogout(true);

  return (
    <Layout backBtn to="/dashboard">
      <Page>
        <SSidebar role="sidebarContainer">
          <SContainer role="profileInfoContainer">
            <div role="imageContainer">
              <ProfilePicture profilePictureFileName={profilePictureFileName} diameter={50} />
            </div>
            <SRowContainers role="infoContainer">
              <SName role="name"> {user?.name} </SName>
              <SInfoContainer role="viewProfileContainer">
                <SInfo to="/profile" role="viewProfile">
                  {t(`viewProfile`)}
                </SInfo>
              </SInfoContainer>
            </SRowContainers>
          </SContainer>
          <SidebarAvailabilityButton />
          <SElementContainer role="elementsList">
            {sidebarElements.map((element, key) => (
              <SidebarElement
                key={key}
                nameTx={element.nameTx}
                e2eId={element.nameTx}
                icon={element.icon}
                rightIcon={element.rightIcon}
                to={element.to}
                onClick={element.onClick}
              />
            ))}
          </SElementContainer>
          {shouldShowActiveTenantBanner && (
            <SMultiOrgBannerSection>
              <MultiOrgActiveBanner tenantName={tenantName} email={email} />
            </SMultiOrgBannerSection>
          )}
          <SFlexDiv />

          {isDevRelease() && isTestApiUrlApplied() && (
            <BigFloatButton
              tx="Clear test API_URL & Log out"
              onClick={handleRemoveTestApiUrl}
            ></BigFloatButton>
          )}
          <SSidebarLogoutFooter>
            <BigFloatButton tx="logout" onClick={confirmLogout} position="level-0" inline />
          </SSidebarLogoutFooter>
        </SSidebar>
      </Page>
    </Layout>
  );
}

export default Sidebar;
